/**
 * Busca global do HUB (Fase 1, F1-06): campo fixo no cabeçalho. Resultados por tipo, só dos tipos que o perfil
 * pode ver (o servidor confere a permissão de cada grupo). ⌘K / Ctrl+K leva o foco ao campo; setas escolhem,
 * Enter abre, Esc fecha. "Ver todos os resultados" abre a página de resultados completa.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.ts';
import { navegar } from '../rotas.ts';
import { Icone } from './icones.tsx';

export interface ItemBusca { titulo: string; detalhe: string; rota: string; motivo?: string }
export interface GrupoBusca { tipo: string; rotulo: string; total: number; itens: ItemBusca[] }

export function BuscaGlobal() {
  const [q, setQ] = useState('');
  const [grupos, setGrupos] = useState<GrupoBusca[] | null>(null);
  const [aberta, setAberta] = useState(false);
  const [sel, setSel] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const ultima = useRef(0);

  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        ref.current?.focus();
        ref.current?.select();
      }
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, []);

  useEffect(() => {
    const termo = q.trim();
    if (termo.length < 2) {
      setGrupos(null);
      return;
    }
    const minha = ++ultima.current;
    setCarregando(true);
    const t = window.setTimeout(async () => {
      try {
        const r = await api.get<{ grupos: GrupoBusca[] }>(`/api/busca?q=${encodeURIComponent(termo)}&limite=5`);
        if (minha === ultima.current) {
          setGrupos(r.grupos);
          setSel(0);
        }
      } catch {
        if (minha === ultima.current) setGrupos([]);
      } finally {
        if (minha === ultima.current) setCarregando(false);
      }
    }, 250);
    return () => window.clearTimeout(t);
  }, [q]);

  const planos = useMemo(() => (grupos ?? []).flatMap((g) => g.itens), [grupos]);
  const total = (grupos ?? []).reduce((s, g) => s + g.total, 0);
  const abrir = (rota: string) => {
    setAberta(false);
    setQ('');
    navegar(rota);
  };
  const verTodos = () => abrir(`/busca?q=${encodeURIComponent(q.trim())}`);

  let k = -1;
  return (
    <div className="busca-global" data-teste="busca-global">
      <label className="busca-global-campo">
        <Icone nome="lupa" />
        <span className="rotulo-oculto">Buscar em todo o HUB</span>
        <input
          ref={ref}
          type="search"
          value={q}
          placeholder="Buscar em todo o HUB…"
          onChange={(e) => {
            setQ(e.target.value);
            setAberta(true);
          }}
          onFocus={() => setAberta(true)}
          onBlur={() => window.setTimeout(() => setAberta(false), 150)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') (setAberta(false), ref.current?.blur());
            if (e.key === 'ArrowDown') (e.preventDefault(), setSel((s) => Math.min(s + 1, planos.length - 1)));
            if (e.key === 'ArrowUp') (e.preventDefault(), setSel((s) => Math.max(s - 1, 0)));
            if (e.key === 'Enter') {
              e.preventDefault();
              if (planos[sel]) abrir(planos[sel].rota);
              else if (q.trim().length >= 2) verTodos();
            }
          }}
          aria-label="Buscar em todo o HUB"
          aria-expanded={aberta && grupos !== null}
          aria-controls="busca-global-resultados"
          role="combobox"
          data-teste="busca-global-campo"
        />
        <kbd aria-hidden="true">⌘ K</kbd>
      </label>
      {aberta && q.trim().length >= 2 && (
        <div className="busca-global-resultados" id="busca-global-resultados" role="listbox" data-teste="busca-global-resultados">
          {carregando && grupos === null && <p className="busca-global-vazio">Buscando…</p>}
          {grupos && grupos.length === 0 && <p className="busca-global-vazio">Nada encontrado para “{q.trim()}”.</p>}
          {(grupos ?? []).map((g) => (
            <div key={g.tipo} className="busca-global-grupo">
              <div className="busca-global-titulo">
                {g.rotulo}
                {g.total > g.itens.length && <span> · {g.total}</span>}
              </div>
              {g.itens.map((it) => {
                k += 1;
                const i = k;
                return (
                  <button key={it.rota + it.titulo} type="button" role="option" aria-selected={i === sel} className={`busca-global-item${i === sel ? ' selecionado' : ''}`} onMouseDown={(e) => e.preventDefault()} onClick={() => abrir(it.rota)} onMouseEnter={() => setSel(i)} data-teste={`busca-resultado-${g.tipo}`}>
                    <span className="busca-global-item-titulo">{it.titulo}</span>
                    <span className="busca-global-item-detalhe">{it.motivo ?? it.detalhe}</span>
                  </button>
                );
              })}
            </div>
          ))}
          {total > 0 && (
            <button type="button" className="busca-global-todos" onMouseDown={(e) => e.preventDefault()} onClick={verTodos} data-teste="busca-ver-todos">
              Ver todos os {total} resultados
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Página de resultados completa da busca global (/busca?q=…). */
export function ResultadosBusca({ q }: { q: string }) {
  const [grupos, setGrupos] = useState<GrupoBusca[] | null>(null);
  const [erro, setErro] = useState('');
  useEffect(() => {
    setGrupos(null);
    setErro('');
    if (q.trim().length < 2) return setGrupos([]);
    api
      .get<{ grupos: GrupoBusca[] }>(`/api/busca?q=${encodeURIComponent(q.trim())}&limite=200`)
      .then((r) => setGrupos(r.grupos))
      .catch((e: Error) => setErro(e.message));
  }, [q]);
  return (
    <div className="resultados-busca" data-teste="resultados-busca">
      <h1 className="titulo-pagina">Resultados para “{q}”</h1>
      {erro && <p className="aviso aviso-erro">{erro}</p>}
      {grupos === null && !erro && <p className="carregando-tela">Buscando…</p>}
      {grupos && grupos.length === 0 && <p className="texto-secundario">Nada encontrado.</p>}
      {(grupos ?? []).map((g) => (
        <section key={g.tipo} className="secao-resultados">
          <h2>
            {g.rotulo} <span className="texto-secundario">({g.total})</span>
          </h2>
          <ul className="lista-resultados">
            {g.itens.map((it) => (
              <li key={it.rota + it.titulo}>
                <a href={`#${it.rota}`}>{it.titulo}</a>
                <span className="texto-secundario">{it.motivo ?? it.detalhe}</span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
